import React, { useEffect, useState } from "react";
import {
  Button,
  Descriptions,
  Divider,
  Empty,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import useOrganization from "../../../../Hooks/useOrganization";
import { useLoader } from "../../../../Context/LoaderContext";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { openNotification } from "../../../../Components/Notification";
import organizationService from "../../../../Service/organization.service";
import { Organization } from "../../../../Entities/Organization";
import "./index.scss";
import SSOConfigurationModal from "./SSOConfigurationModal";

const SSO: React.FC = () => {
  const { org, setOrg } = useOrganization();
  const { isLoading, setLoading, setLoadingMessage } = useLoader();

  const [showSSOConfigurationModal, setShowSSOConfigurationModal] =
    useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);

  useEffect(() => {
    if (!org) {
      setLoading(true);
      setLoadingMessage("Loading organization details...");
    } else {
      setLoading(false);
      setLoadingMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  const isSSOEnabled = !!(
    org?.oauthProviderUrl &&
    org?.oauthTokenUrl &&
    org?.oauthClientId &&
    org?.oauthClientSecret &&
    org?.oauthScopes &&
    org?.oauthUserInfoUrl
  );

  const handleSSOConfigurationModalOpen = () => {
    setShowSSOConfigurationModal(true);
  };

  const handleCopyClientSecret = async () => {
    if (!org?.oauthClientSecret) {
      openNotification("error", "Error", "No client secret available to copy");
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(org.oauthClientSecret);
        openNotification(
          "success",
          "Copied",
          "Client secret copied to clipboard"
        );
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = org.oauthClientSecret;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            openNotification(
              "success",
              "Copied",
              "Client secret copied to clipboard"
            );
          } else {
            throw new Error("Copy command failed");
          }
        } catch (err) {
          throw new Error("Copy command failed");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      openNotification(
        "error",
        "Failed to copy client secret",
        "Please copy the client secret manually"
      );
    }
  };

  const handleRemoveSSOConfiguration = () => {
    if (!org) return;

    Modal.confirm({
      title: "Remove SSO Configuration",
      content:
        "Are you sure you want to remove all SSO configuration? This action cannot be undone.",
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          setLoadingMessage("Removing SSO configuration...");

          const updatedOrg = await organizationService.updateOrganization({
            _id: org._id,
            oauthProviderUrl: null,
            oauthTokenUrl: null,
            oauthClientId: null,
            oauthClientSecret: null,
            oauthScopes: null,
            oauthUserInfoUrl: null,
            oauthVersion: null,
          } as any);

          if (updatedOrg) {
            setOrg(updatedOrg);
            openNotification(
              "success",
              "Success",
              "SSO configuration removed successfully"
            );
          }
        } catch (error: any) {
          console.error("Error removing SSO configuration:", error);
          openNotification(
            "error",
            "Error",
            error.message || "Failed to remove SSO configuration"
          );
        } finally {
          setLoading(false);
          setLoadingMessage(null);
        }
      },
    });
  };

  return (
    <section className="integration-tab-content-section">
      <div className="integration-details-section-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Single Sign-on
        </Typography.Title>

        <Divider style={{ margin: "0" }} />
      </div>
      <section className="integration-tab-content-section-content">
        <Typography.Paragraph>
          <strong>QualifyMe</strong> SSO is used to authenticate and enable
          single sign-on with QualifyMe services.
        </Typography.Paragraph>

        <div className="sub-section-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            SSO Configuration
          </Typography.Title>

          {isSSOEnabled && (
            <Space>
              <Button
                type="primary"
                icon={<EditOutlined />}
                loading={isLoading}
                onClick={handleSSOConfigurationModalOpen}
              >
                Edit
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={isLoading}
                onClick={handleRemoveSSOConfiguration}
              >
                Remove
              </Button>
            </Space>
          )}
        </div>

        {!isSSOEnabled && (
          <Empty
            description={
              <span>
                Looks like you haven't configured SSO yet.{" "}
                <Typography.Link onClick={handleSSOConfigurationModalOpen}>
                  Click here to configure
                </Typography.Link>
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {isSSOEnabled && org && (
          <div className="sso-configuration-display">
            <Descriptions
              bordered
              column={1}
              size="small"
              labelStyle={{ fontWeight: 600, width: "200px" }}
            >
              <Descriptions.Item label="OAuth Provider URL">
                <Typography.Text copyable>
                  {org.oauthProviderUrl}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="OAuth Token URL">
                <Typography.Text copyable>{org.oauthTokenUrl}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="OAuth Client ID">
                <Typography.Text copyable>{org.oauthClientId}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="OAuth Client Secret">
                <Space>
                  <Typography.Text>
                    {org.oauthClientSecret
                      ? showClientSecret
                        ? org.oauthClientSecret
                        : "•".repeat(Math.min(org.oauthClientSecret.length, 20))
                      : "Not set"}
                  </Typography.Text>
                  {org.oauthClientSecret && (
                    <>
                      <Button
                        type="link"
                        size="small"
                        icon={
                          showClientSecret ? (
                            <EyeInvisibleOutlined />
                          ) : (
                            <EyeOutlined />
                          )
                        }
                        onClick={() => setShowClientSecret(!showClientSecret)}
                        style={{ padding: "0 4px" }}
                      />
                      <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={handleCopyClientSecret}
                        style={{ padding: "0 4px" }}
                      />
                    </>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="OAuth Scopes">
                {org.oauthScopes && org.oauthScopes.length > 0 ? (
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    {org.oauthScopes.map((scope, index) => (
                      <Tag key={index}>{scope}</Tag>
                    ))}
                  </div>
                ) : (
                  <Typography.Text type="secondary">
                    No scopes configured
                  </Typography.Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="OAuth User Info URL">
                <Typography.Text copyable>
                  {org.oauthUserInfoUrl}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="OAuth Version">
                <Typography.Text>
                  {org.oauthVersion || "Not set"}
                </Typography.Text>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </section>

      {org && (
        <SSOConfigurationModal
          open={showSSOConfigurationModal}
          setOpen={setShowSSOConfigurationModal}
          org={org}
          isSSOEnabled={isSSOEnabled}
          setOrg={setOrg}
        />
      )}
    </section>
  );
};

export default SSO;
